<template>
    <PageHeader :title="headerTitle" back-to="/cash-register"/>

    <div v-if="cash" class="cash-summary-card">
        <div class="cash-summary-header">
            <div class="cash-summary-header-text">
                <span class="cash-summary-title">{{ $t('pages.cash_register.detail.balanceTitle') }}</span>
                <span class="cash-summary-name">
                    {{ cash.name }}
                    <span v-if="cash.is_default" class="cash-default-pill">{{ $t('pages.cash_register.defaultBadge') }}</span>
                </span>
            </div>
            <div class="cash-summary-net">
                <span class="cash-summary-net-label">{{ $t('pages.cash_register.detail.netBalanceLabel') }}</span>
                <span class="cash-summary-net-value" :class="netBalanceClass">{{ formatTRY(cash.net_balance) }}</span>
            </div>
        </div>

        <div class="cash-summary-metrics">
            <div class="cash-metric cash-metric--in">
                <span class="cash-metric-label">{{ $t('pages.cash_register.cols.income') }}</span>
                <span class="cash-metric-value">{{ formatTRY(cash.transaction_income) }}</span>
            </div>
            <div class="cash-metric cash-metric--out">
                <span class="cash-metric-label">{{ $t('pages.cash_register.cols.expense') }}</span>
                <span class="cash-metric-value">{{ formatTRY(cash.transaction_expense) }}</span>
            </div>
            <div class="cash-metric cash-metric--in">
                <span class="cash-metric-label">{{ $t('pages.cash_register.cols.manualIn') }}</span>
                <span class="cash-metric-value">{{ formatTRY(cash.manual_in) }}</span>
            </div>
            <div class="cash-metric cash-metric--out">
                <span class="cash-metric-label">{{ $t('pages.cash_register.cols.manualOut') }}</span>
                <span class="cash-metric-value">{{ formatTRY(cash.manual_out) }}</span>
            </div>
        </div>

        <div class="cash-summary-foot">
            <span class="cash-summary-foot-label">{{ $t('pages.cash_register.cols.manualBalance') }}:</span>
            <span class="cash-summary-foot-value">{{ formatTRY(cash.net_balance_without_transaction) }}</span>
            <span class="cash-summary-foot-hint">· {{ $t('pages.cash_register.detail.manualBalanceHint') }}</span>
        </div>
    </div>

    <custom-table
        :title="$t('pages.cash_register.movement.title')"
        :subTitle="$t('pages.cash_register.movement.subTitle')"
        rowKey="id"
        :items="movementTable.data"
        :loading="movementTable.loading"
        :columns="movementFields"
        :actions="movementActions"
        :pagination="movementTable.pagination"
        :sortMeta="movementTable.pagination.sort || []"
        changeUrlPage
        @action="handleMovementAction"
        @changeTable="handleMovementTableChange"
        @sort="handleMovementSort"
    >
        <template v-slot:direction="{ row: record }">
            <span class="cash-direction" :class="record.direction_id === 1 ? 'cash-direction--in' : 'cash-direction--out'">
                <svg v-if="record.direction_id === 1" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="12" y1="19" x2="12" y2="5"/>
                    <polyline points="5 12 12 5 19 12"/>
                </svg>
                <svg v-else width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <polyline points="19 12 12 19 5 12"/>
                </svg>
                {{ $t('pages.cash_register.movement.directionType.' + record.direction_id) }}
            </span>
        </template>

        <template v-slot:amount="{ row: record }">
            <span class="cash-mvm-amount" :class="record.direction_id === 1 ? 'cash-balance--in' : 'cash-balance--out'">
                {{ record.direction_id === 1 ? '+' : '−' }} {{ formatTRY(record.amount) }}
            </span>
        </template>

        <template v-slot:note="{ row: record }">
            <span v-if="record.note" class="cash-mvm-note">{{ record.note }}</span>
            <span v-else class="cash-mvm-muted">—</span>
        </template>

        <template v-slot:created_at="{ row: record }">
            {{ formatDate(record.created_at) }}
        </template>
    </custom-table>

    <p class="cash-mvm-audit">{{ $t('pages.cash_register.movement.auditNote') }}</p>

    <CashMovementDialog ref="movementDialog" :cash-id="cashId" @created="onMovementCreated"/>
</template>

<script>
import CustomTable from "@/components/custom-table";
import PageHeader from "@/components/page-header/PageHeader.vue";
import CashMovementDialog from "@/components/cash-register/CashMovementDialog.vue";
import transactionService from "@/components/payment/transactionService";

export default {
    name: "cash-register-detail",
    components: { CustomTable, PageHeader, CashMovementDialog },
    data() {
        return {
            cash: null,
            movementFields: [
                { name: this.$t("pages.cash_register.movement.cols.direction"), sortingField: "direction_id", sortable: true, scopedSlots: { customRender: "direction" } },
                { name: this.$t("pages.cash_register.movement.cols.amount"), sortingField: "amount", sortable: true, scopedSlots: { customRender: "amount" } },
                { name: this.$t("pages.cash_register.movement.cols.note"), scopedSlots: { customRender: "note" } },
                { name: this.$t("pages.cash_register.movement.cols.createdAt"), sortingField: "created_at", sortable: true, scopedSlots: { customRender: "created_at" } },
            ],
            movementActions: [
                { name: "new", icon: "bi-plus-lg", label: "btn.add" },
                { name: "refresh", icon: "bi-arrow-repeat", label: "btn.refresh" },
            ],
            movementFilterParams: { sort: 'id:desc' },
        };
    },
    computed: {
        cashId() {
            return this.$route.params.id;
        },
        headerTitle() {
            if (this.cash) {
                return this.sprintf(this.$t("pages.cash_register.detailCash"), [this.cash.name]);
            }
            return this.$t('pages.cash_register.detail.title');
        },
        movementTable() {
            return this.$store.state.cash.movement.table;
        },
        netBalanceClass() {
            const n = parseFloat(this.cash?.net_balance);
            if (isNaN(n) || n === 0) return 'cash-summary-net-value--neutral';
            return n > 0 ? 'cash-summary-net-value--positive' : 'cash-summary-net-value--negative';
        },
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t("pages.cash_register.detail.title"), []);
        this.loadCash();
        this.refreshMovements();
    },
    methods: {
        formatTRY(amount) {
            const n = parseFloat(amount);
            if (isNaN(n)) return '-';
            return n.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 });
        },
        formatDate(d) {
            if (!d) return '-';
            const dt = new Date(d);
            if (isNaN(dt.getTime())) return d;
            return dt.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                + ' · ' + dt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        },
        async loadCash() {
            await this.$store.dispatch('cash/show', { id: this.cashId });
            this.cash = this.$store.state.cash.save?.data || null;
        },
        refreshMovements(pagination) {
            this.$store.dispatch('cash/movement/get', {
                cashId: this.cashId,
                page: pagination,
                filterData: this.movementFilterParams,
            });
        },
        handleMovementAction(name) {
            if (name === 'refresh') {
                this.loadCash();
                this.refreshMovements();
            } else if (name === 'new') {
                this.$refs.movementDialog?.open();
            }
        },
        handleMovementTableChange(pagination, filterData) {
            const currentSort = this.movementFilterParams.sort || 'id:desc';
            this.movementFilterParams = { ...filterData, sort: currentSort };
            this.refreshMovements(pagination);
        },
        handleMovementSort({ columnName, order }) {
            this.movementFilterParams = {
                ...this.movementFilterParams,
                sort: `${columnName}:${order}`,
            };
            this.refreshMovements(this.movementTable.pagination);
        },
        onMovementCreated() {
            this.loadCash();
            this.refreshMovements();
            transactionService.refreshCashRegisters();
        },
    },
};
</script>

<style scoped>
.cash-summary-card {
    margin: 12px 0 18px;
    padding: 22px 24px;
    border-radius: 14px;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.04);
}

.cash-summary-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.cash-summary-header-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.cash-summary-title {
    font-size: 11px;
    font-weight: 700;
    color: #8e8e93;
    text-transform: uppercase;
    letter-spacing: 0.6px;
}

.cash-summary-name {
    font-size: 18px;
    font-weight: 700;
    color: #1c1c1e;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.cash-default-pill {
    display: inline-flex;
    padding: 2px 9px;
    border-radius: 999px;
    background: rgba(var(--panel-accent-rgb, 0, 122, 255), 0.12);
    color: var(--panel-accent-text, #007AFF);
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.cash-summary-net {
    display: flex;
    flex-direction: column;
    gap: 2px;
    align-items: flex-end;
}

.cash-summary-net-label {
    font-size: 11px;
    font-weight: 600;
    color: #8e8e93;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.cash-summary-net-value {
    font-size: 26px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
    letter-spacing: -0.4px;
}

.cash-summary-net-value--positive { color: #28a745; }
.cash-summary-net-value--negative { color: #ef4444; }
.cash-summary-net-value--neutral { color: #6b7280; }

.cash-summary-metrics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.cash-metric {
    position: relative;
    padding: 14px 16px 14px 18px;
    border-radius: 11px;
    background: rgba(0, 0, 0, 0.025);
    border: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow: hidden;
}

.cash-metric::before {
    content: '';
    position: absolute;
    left: 0;
    top: 12px;
    bottom: 12px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: currentColor;
    opacity: 0.55;
}

.cash-metric-label {
    font-size: 10.5px;
    font-weight: 700;
    color: #8e8e93;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.cash-metric-value {
    font-size: 16px;
    font-weight: 700;
    color: #1c1c1e;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
}

.cash-metric--in { color: #28a745; }
.cash-metric--out { color: #ef4444; }

.cash-summary-foot {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    font-size: 12px;
    color: #8e8e93;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.cash-summary-foot-label { font-weight: 600; }

.cash-summary-foot-value {
    font-weight: 700;
    color: #1c1c1e;
    font-variant-numeric: tabular-nums;
}

.cash-summary-foot-hint { opacity: 0.8; font-size: 11.5px; }


/* Dark / midnight overrides */
html.dark .cash-summary-card {
    background: #15171C;
    border-color: rgba(255, 255, 255, 0.07);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.25);
}

html.midnight .cash-summary-card {
    background: #1A1714;
    border-color: rgba(255, 255, 255, 0.07);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3), 0 8px 24px rgba(0, 0, 0, 0.25);
}

html.dark .cash-summary-title,
html.midnight .cash-summary-title,
html.dark .cash-summary-net-label,
html.midnight .cash-summary-net-label,
html.dark .cash-metric-label,
html.midnight .cash-metric-label,
html.dark .cash-summary-foot,
html.midnight .cash-summary-foot { color: #9A9CAE; }

html.dark .cash-summary-name,
html.midnight .cash-summary-name,
html.dark .cash-metric-value,
html.midnight .cash-metric-value,
html.dark .cash-summary-foot-value,
html.midnight .cash-summary-foot-value { color: #EAE6E1; }

html.dark .cash-summary-net-value--positive,
html.midnight .cash-summary-net-value--positive { color: #34D399; }
html.dark .cash-summary-net-value--negative,
html.midnight .cash-summary-net-value--negative { color: #F87171; }
html.dark .cash-summary-net-value--neutral,
html.midnight .cash-summary-net-value--neutral { color: #9CA3AF; }

html.dark .cash-metric,
html.midnight .cash-metric {
    background: rgba(255, 255, 255, 0.025);
    border-color: rgba(255, 255, 255, 0.05);
}

html.dark .cash-metric--in,
html.midnight .cash-metric--in { color: #34D399; }
html.dark .cash-metric--out,
html.midnight .cash-metric--out { color: #F87171; }

html.dark .cash-summary-foot,
html.midnight .cash-summary-foot { border-top-color: rgba(255, 255, 255, 0.06); }


/* Cream */
html.cream .cash-summary-card {
    background: #FEFCF8;
    border-color: rgba(61, 56, 50, 0.1);
}

html.cream .cash-summary-title,
html.cream .cash-summary-net-label,
html.cream .cash-metric-label,
html.cream .cash-summary-foot { color: #847C72; }

html.cream .cash-summary-name,
html.cream .cash-metric-value,
html.cream .cash-summary-foot-value { color: #3D3832; }

html.cream .cash-metric {
    background: rgba(61, 56, 50, 0.04);
    border-color: rgba(61, 56, 50, 0.08);
}

html.cream .cash-metric--in,
html.cream .cash-summary-net-value--positive { color: #059669; }


/* Movement table cells */
.cash-direction {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 600;
}

.cash-direction--in { background: rgba(40, 167, 69, 0.12); color: #28a745; }
.cash-direction--out { background: rgba(239, 68, 68, 0.12); color: #ef4444; }

html.dark .cash-direction--in,
html.midnight .cash-direction--in { color: #34D399; }
html.dark .cash-direction--out,
html.midnight .cash-direction--out { color: #F87171; }

.cash-mvm-amount {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    font-size: 13.5px;
}

.cash-balance--in { color: #28a745; }
.cash-balance--out { color: #ef4444; }

html.dark .cash-balance--in,
html.midnight .cash-balance--in { color: #34D399; }
html.dark .cash-balance--out,
html.midnight .cash-balance--out { color: #F87171; }

.cash-mvm-note {
    font-size: 12.5px;
    color: #1c1c1e;
}

.cash-mvm-muted {
    color: #c7c7cc;
}

html.dark .cash-mvm-note,
html.midnight .cash-mvm-note { color: #EAE6E1; }

.cash-mvm-audit {
    margin-top: 10px;
    padding: 10px 14px;
    border-radius: 8px;
    background: rgba(var(--panel-accent-rgb, 0, 122, 255), 0.05);
    color: #8e8e93;
    font-size: 11.5px;
    line-height: 1.5;
}

html.dark .cash-mvm-audit,
html.midnight .cash-mvm-audit {
    background: rgba(var(--panel-accent-rgb), 0.08);
    color: #9A9CAE;
}


@media (max-width: 720px) {
    .cash-summary-metrics {
        grid-template-columns: repeat(2, 1fr);
    }
    .cash-summary-net-value {
        font-size: 22px;
    }
}

@media (max-width: 480px) {
    .cash-summary-metrics {
        grid-template-columns: 1fr;
    }
    .cash-summary-header {
        flex-direction: column;
    }
    .cash-summary-net {
        align-items: flex-start;
    }
}
</style>
